import React from 'react';
import { Drawer, Box, Typography, IconButton, Divider } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Markdown } from '@qwickapps/react-framework';

interface MarkdownCheatsheetProps {
  open: boolean;
  onClose: () => void;
}

const cheatsheetContent = `# Markdown Cheatsheet

Complete reference for all supported markdown features in QwickPDF.

---

## Headings

\`\`\`markdown
# Heading 1
## Heading 2
### Heading 3
#### Heading 4
\`\`\`

# Heading 1
## Heading 2
### Heading 3
#### Heading 4

---

## Text Formatting

\`\`\`markdown
**Bold text**
*Italic text*
***Bold and italic***
~~Strikethrough~~
\`Inline code\`
\`\`\`

**Bold text**
*Italic text*
***Bold and italic***
~~Strikethrough~~
\`Inline code\`

---

## Lists

### Unordered Lists

\`\`\`markdown
- First item
- Second item
  - Nested item
  - Another nested
- Third item
\`\`\`

- First item
- Second item
  - Nested item
  - Another nested
- Third item

### Ordered Lists

\`\`\`markdown
1. First step
2. Second step
3. Third step
\`\`\`

1. First step
2. Second step
3. Third step

---

## Links

\`\`\`markdown
[Link text](https://example.com)
[QwickApps](https://qwickapps.com)
\`\`\`

[Link text](https://example.com)
[QwickApps](https://qwickapps.com)

---

## Code

### Inline Code

\`\`\`markdown
Use \`backticks\` for inline code
\`\`\`

Use \`backticks\` for inline code

### Code Blocks

\`\`\`markdown
\\\`\\\`\\\`javascript
function createPDF() {
  console.log("Creating PDF...");
  return document;
}
\\\`\\\`\\\`
\`\`\`

\`\`\`javascript
function createPDF() {
  console.log("Creating PDF...");
  return document;
}
\`\`\`

---

## Blockquotes

\`\`\`markdown
> This is a blockquote
> It can span multiple lines
>
> — Author Name
\`\`\`

> This is a blockquote
> It can span multiple lines
>
> — Author Name

---

## Tables

\`\`\`markdown
| Column 1 | Column 2 | Column 3 |
|----------|----------|----------|
| Data 1   | Data 2   | Data 3   |
| Data 4   | Data 5   | Data 6   |
\`\`\`

| Column 1 | Column 2 | Column 3 |
|----------|----------|----------|
| Data 1   | Data 2   | Data 3   |
| Data 4   | Data 5   | Data 6   |

---

## Horizontal Rules

\`\`\`markdown
---
***
___
\`\`\`

Use three or more dashes, asterisks, or underscores.

---

## Tips

- **Auto-Save**: Your work saves automatically
- **Copy/Paste**: Works as expected
- **Undo/Redo**: Standard keyboard shortcuts
- **Preview**: Updates as you type
- **Export**: Download as PDF anytime

---

## Pro Tips

1. Use **consistent heading levels** for better structure
2. Add **blank lines** between sections for readability
3. Use **bold and italic** sparingly for emphasis
4. Tables work best with **short content**
5. Code blocks preserve **formatting and indentation**

**Happy writing!**
`;

export const MarkdownCheatsheet: React.FC<MarkdownCheatsheetProps> = ({ open, onClose }) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 480 },
          backgroundColor: 'var(--theme-background)',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid var(--theme-divider)',
          }}
        >
          <Typography variant="h6" sx={{ color: 'var(--theme-text)', fontWeight: 600 }}>
            Markdown Cheatsheet
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3,
            fontSize: '0.9rem',
            '& h1': {
              color: 'var(--theme-primary)',
              fontSize: '1.75rem',
              fontWeight: 700,
              mt: 0,
              mb: 2,
            },
            '& h2': {
              color: 'var(--theme-text)',
              fontSize: '1.25rem',
              fontWeight: 600,
              mt: 3,
              mb: 1.5,
            },
            '& h3': {
              color: 'var(--theme-text)',
              fontSize: '1.1rem',
              fontWeight: 600,
              mt: 2,
              mb: 1,
            },
            '& p': {
              mb: 1.5,
              lineHeight: 1.6,
              color: 'var(--theme-text-secondary)',
            },
            '& code': {
              backgroundColor: 'var(--theme-surface)',
              padding: '2px 6px',
              borderRadius: '3px',
              fontSize: '0.85em',
            },
            '& pre': {
              backgroundColor: 'var(--theme-surface)',
              padding: 2,
              borderRadius: 1,
              overflow: 'auto',
              fontSize: '0.85rem',
            },
            '& ul, & ol': {
              mb: 1.5,
              pl: 3,
            },
            '& li': {
              mb: 0.5,
              color: 'var(--theme-text-secondary)',
            },
            '& hr': {
              my: 3,
              borderColor: 'var(--theme-divider)',
            },
            '& table': {
              width: '100%',
              mb: 2,
              borderCollapse: 'collapse',
            },
            '& th, & td': {
              border: '1px solid var(--theme-divider)',
              padding: '8px',
              textAlign: 'left',
            },
            '& th': {
              backgroundColor: 'var(--theme-surface)',
              fontWeight: 600,
            },
            '& blockquote': {
              borderLeft: '4px solid var(--theme-primary)',
              pl: 2,
              ml: 0,
              fontStyle: 'italic',
              color: 'var(--theme-text-secondary)',
            },
            '& strong': {
              color: 'var(--theme-text)',
              fontWeight: 600,
            },
          }}
        >
          <Markdown sanitize={true}>{cheatsheetContent}</Markdown>
        </Box>
      </Box>
    </Drawer>
  );
};
